import { useQuery } from "@tanstack/react-query";
import { api } from "./client";

// External Portal v2 PR 3 (PROB-17 / PROB-24a) — internal-staff directory
// client. Backs the multi-select InternalUserPicker (search) and the batched
// id -> label resolver (labels) used by the assignee chips on the queue,
// detail header, and Manage-Assignees panel. Mirrors the useOwnerLabels shape
// so consumer code reads in parallel.

export interface InternalUser {
  id: string;
  displayName: string;
  email: string;
  roleCode: string | null;
  active: boolean;
}

export const internalUsersApi = {
  search: (q: string, active = true) =>
    api.get<InternalUser[]>("/internal/users/search", { q, active }),
  labels: (ids: string[]) =>
    api.get<InternalUser[]>("/internal/users/labels", { ids: ids.join(",") }),
};

/**
 * Batched id -> label resolver. Pass any number of user ids; the hook
 * dedupes, caches by the sorted id-set, and returns a
 * `(id) => InternalUser | undefined` lookup plus an isLoading flag.
 *
 * Fallback rendering: when the lookup hasn't resolved an id yet (or the user
 * is missing), the caller should show `fallbackUserLabel(id)` — never a raw
 * GUID.
 */
export function useInternalUserLabels(userIds: string[]) {
  const cleaned = Array.from(new Set(userIds.filter(Boolean)));
  const key = [...cleaned].sort().join("|");
  const query = useQuery({
    queryKey: ["internal-user-labels", key],
    queryFn: () => internalUsersApi.labels(cleaned),
    enabled: cleaned.length > 0,
    staleTime: 60_000,
  });
  const map = new Map<string, InternalUser>();
  for (const u of query.data ?? []) {
    map.set(u.id, u);
  }
  return {
    lookup: (id: string): InternalUser | undefined => map.get(id),
    isLoading: query.isLoading,
  };
}

/** Default rendering when a user id hasn't been resolved yet. Never a raw GUID. */
export function fallbackUserLabel(id: string): string {
  return id.length > 8 ? `User ${id.slice(0, 8)}` : id;
}
